import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, TrendingUp, LineChart, Calendar, CalendarRange, DollarSign, Users, ShoppingBag, ArrowUpRight, ArrowDownRight, ArrowRight, Wallet } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Booking } from '../lib/bookings';
import { BookingService } from '../types/booking';
import {
  fetchStandaloneInvoiceSales,
  sumStandaloneInvoiceSales,
  type StandaloneInvoiceSale,
} from '../lib/invoices/standaloneInvoiceSales';
import { useBusiness } from '../contexts/BusinessContext';
import PortalDropdown from '../components/ui/PortalDropdown';

// Extended interface to include the additional properties from the database
interface ExtendedBookingService {
  id: string;
  booking_id: string;
  service_id: string;
  service_name: string;
  service_category_name?: string;
  category_name?: string;
  sellable_item_id?: string;
  name?: string;
  total_amount?: number;
  price: number;
  duration: number;
  discount_amount?: number;
  created_at: string;
  updated_at: string;
}
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const reportPages = [
  { id: 'sales', name: 'Sales', icon: TrendingUp, path: '/sales-reports/sales' },
  { id: 'profit-loss', name: 'Profit & Loss', icon: LineChart, path: '/sales-reports/profit-loss' }
];

// Mock data for the dashboard
const mockMetrics = [
  {
    id: 'total-sales',
    name: 'Total Sales',
    value: '$24,567.89',
    change: '+12.5%',
    trend: 'up',
    icon: DollarSign
  },
  {
    id: 'customers',
    name: 'New Customers',
    value: '145',
    change: '+8.2%',
    trend: 'up',
    icon: Users
  },
  {
    id: 'orders',
    name: 'Total Orders',
    value: '289',
    change: '-3.1%',
    trend: 'down',
    icon: ShoppingBag
  }
];

const mockRecentSales = [
  { id: 1, customer: 'John Doe', service: 'Premium Package', amount: '$1,200', date: '2024-04-10', status: 'completed' },
  { id: 2, customer: 'Jane Smith', service: 'Basic Service', amount: '$400', date: '2024-04-09', status: 'pending' },
  { id: 3, customer: 'Mike Johnson', service: 'Consultation', amount: '$150', date: '2024-04-09', status: 'completed' },
  { id: 4, customer: 'Sarah Williams', service: 'Advanced Package', amount: '$800', date: '2024-04-08', status: 'completed' },
  { id: 5, customer: 'Tom Brown', service: 'Custom Solution', amount: '$650', date: '2024-04-08', status: 'pending' }
];

const dateRanges = [
  { id: 'this-month', name: 'This Month' },
  { id: 'last-month', name: 'Last Month' },
  { id: 'next-month', name: 'Next Month' },
  { id: 'custom', name: 'Custom Range', icon: CalendarRange }
];

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { 
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const getDateRangeText = (rangeId: string, startDate?: string, endDate?: string): string => {
  // Helper to create a UTC date that represents local midnight
  const createLocalDate = (date: Date | string) => {
    if (typeof date === 'string') {
      // For custom range dates, which come in as YYYY-MM-DD
      return new Date(date + 'T00:00:00Z');
    }
    // For other dates, ensure we're working with UTC midnight
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ));
  };

  // Helper to get Monday (1) to Sunday (0) based week dates
  const getWeekDates = (baseDate: Date) => {
    const date = createLocalDate(baseDate);
    const day = date.getUTCDay();
    const diff = date.getUTCDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    
    const monday = new Date(Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      diff
    ));
    
    const sunday = new Date(Date.UTC(
      monday.getUTCFullYear(),
      monday.getUTCMonth(),
      monday.getUTCDate() + 6
    ));
    
    return { monday, sunday };
  };

  const now = new Date();
  const utcNow = createLocalDate(now);

  switch (rangeId) {
    case 'this-month': {
      const monthName = now.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
      return monthName;
    }
    case 'last-month': {
      const lastMonth = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
      return lastMonth.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    }
    case 'next-month': {
      const nextMonth = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
      return nextMonth.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
    }
    case 'this-week': {
      const { monday, sunday } = getWeekDates(now);
      return `${formatDate(monday)} - ${formatDate(sunday)}`;
    }
    case 'last-week': {
      const lastWeek = new Date(Date.UTC(
        utcNow.getUTCFullYear(),
        utcNow.getUTCMonth(),
        utcNow.getUTCDate() - 7
      ));
      const { monday, sunday } = getWeekDates(lastWeek);
      return `${formatDate(monday)} - ${formatDate(sunday)}`;
    }
    case 'next-week': {
      const nextWeek = new Date(Date.UTC(
        utcNow.getUTCFullYear(),
        utcNow.getUTCMonth(),
        utcNow.getUTCDate() + 7
      ));
      const { monday, sunday } = getWeekDates(nextWeek);
      return `${formatDate(monday)} - ${formatDate(sunday)}`;
    }
    case 'custom': {
      if (startDate && endDate) {
        const start = createLocalDate(startDate);
        const end = createLocalDate(endDate);
        return `${formatDate(start)} - ${formatDate(end)}`;
      }
      return 'Custom Range';
    }
    default:
      return 'Select Date Range';
  }
};

// Function to check if a date falls within the selected range
const isDateInRange = (date: string | null | undefined, rangeId: string, customStartDate?: string, customEndDate?: string): boolean => {
  if (!date) return false;
  
  // Convert the booking end date to UTC midnight
  const targetDate = new Date(date + 'T00:00:00Z');
  const now = new Date();
  const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

  switch (rangeId) {
    case 'this-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'last-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'next-month': {
      const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
      const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 2, 0, 23, 59, 59, 999));
      return targetDate >= start && targetDate <= end;
    }
    case 'custom': {
      if (customStartDate && customEndDate) {
        // Convert custom dates to UTC midnight
        const start = new Date(customStartDate + 'T00:00:00Z');
        const end = new Date(customEndDate + 'T23:59:59.999Z');
        return targetDate >= start && targetDate <= end;
      }
      return false;
    }
    default:
      return false;
  }
};

interface ServiceCategory {
  id: string;
  name: string;
  totalSales: number;
  services: {
    id: string;
    name: string;
    sales: number;
    revenue: number;
  }[];
}

interface ServiceEntry {
  id: string;
  name: string;
  revenue: number;
  count: number;
}

const processBookingServices = (services: ExtendedBookingService[]) => {
  const servicesByCategory = services.reduce<Record<string, ServiceEntry[]>>((acc, service) => {
    // Determine category name based on whether it's a service or sellable item
    const categoryName = service.service_id 
      ? (service.service_category_name || 'Uncategorized Services')
      : (service.category_name || 'Uncategorized Items');

    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }

    // Create service entry
    const serviceEntry: ServiceEntry = {
      id: service.service_id || service.sellable_item_id || service.id,
      name: service.name || service.service_name || 'Unnamed Service',
      revenue: service.total_amount || service.price || 0,
      count: 1
    };

    // Find existing service entry or add new one
    const existingServiceIndex = acc[categoryName].findIndex(s => 
      service.service_id 
        ? s.name === serviceEntry.name // For services, group by name
        : s.id === service.id    // For items, group by ID to avoid combining different items with same name
    );

    if (existingServiceIndex >= 0) {
      acc[categoryName][existingServiceIndex].revenue += serviceEntry.revenue;
      acc[categoryName][existingServiceIndex].count += 1;
    } else {
      acc[categoryName].push(serviceEntry);
    }

    return acc;
  }, {});

  // Calculate totals and sort
  const categories = Object.entries(servicesByCategory).map(([name, services]) => {
    const totalSales = services.reduce((sum, service) => sum + service.count, 0);
    const totalRevenue = services.reduce((sum, service) => sum + service.revenue, 0);

    return {
      name,
      services: services.sort((a, b) => b.revenue - a.revenue),
      totalSales,
      totalRevenue
    };
  });

  // Sort categories by total revenue
  return categories.sort((a, b) => b.totalRevenue - a.totalRevenue);
};

// Function to fetch and calculate sales data
const useSalesData = (selectedDateRange: typeof dateRanges[0], customStartDate?: string, customEndDate?: string, businessId?: string) => {
  const [salesData, setSalesData] = useState({
    totalSales: 0,
    salesChange: 0,
    totalAfterDiscounts: 0,
    afterDiscountsChange: 0,
    totalOrders: 0,
    orderChange: 0,
    bookings: [] as Booking[]
  });

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        if (!businessId) {
          console.warn('No business ID provided for sales data');
          return;
        }

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 2);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth());
            previousPeriodStart.setDate(1);
            break;
          case 'this-week':
            currentPeriodStart.setDate(currentPeriodStart.getDate() - currentPeriodStart.getDay() + (currentPeriodStart.getDay() === 0 ? -6 : 1));
            previousPeriodStart.setDate(previousPeriodStart.getDate() - 7 - previousPeriodStart.getDay() + (previousPeriodStart.getDay() === 0 ? -6 : 1));
            break;
          case 'last-week':
            currentPeriodStart.setDate(currentPeriodStart.getDate() - 7 - currentPeriodStart.getDay() + (currentPeriodStart.getDay() === 0 ? -6 : 1));
            previousPeriodStart.setDate(previousPeriodStart.getDate() - 14 - previousPeriodStart.getDay() + (previousPeriodStart.getDay() === 0 ? -6 : 1));
            break;
          case 'next-week':
            currentPeriodStart.setDate(currentPeriodStart.getDate() + 7 - currentPeriodStart.getDay() + (currentPeriodStart.getDay() === 0 ? -6 : 1));
            previousPeriodStart.setDate(previousPeriodStart.getDate() - previousPeriodStart.getDay() + (previousPeriodStart.getDay() === 0 ? -6 : 1));
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              // For custom range, calculate previous period of same length
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Fetch all bookings with end dates and include discounts and services with category details
        const { data: bookings, error } = await supabase
          .from('bookings')
          .select(`
            *,
            customer:customers!bookings_customer_id_fkey(
              id,
              created_at
            ),
            discounts:booking_discounts(*),
            services:booking_services_with_details(
              *,
              service_category_name
            )
          `)
          .eq('business_id', businessId)
          .eq('is_deleted', false)
          .order('end_date', { ascending: false });

        if (error) throw error;

        // Filter bookings for current period based on end_date and exclude cancelled bookings
        const currentPeriodBookings = (bookings as Booking[]).filter(booking => 
          isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
          booking.status !== 'cancelled'
        );

        // Filter bookings for previous period based on end_date and exclude cancelled bookings
        const previousPeriodBookings = (bookings as Booking[]).filter(booking => {
          if (!booking.end_date) return false;
          const bookingDate = new Date(booking.end_date + 'T00:00:00Z');
          return bookingDate >= previousPeriodStart && 
                 bookingDate < currentPeriodStart &&
                 booking.status !== 'cancelled';
        });

        // Standalone (booking-less) invoices count as sales — attributed to
        // their issue date, same period rules as bookings.
        const standaloneAll = await fetchStandaloneInvoiceSales(businessId);
        const invoiceDay = (inv: StandaloneInvoiceSale) =>
          new Date(inv.finalizedAt).toLocaleDateString('en-CA'); // yyyy-MM-dd local
        const currentStandalone = standaloneAll.filter((inv) =>
          isDateInRange(invoiceDay(inv), selectedDateRange.id, customStartDate, customEndDate),
        );
        const previousStandalone = standaloneAll.filter((inv) => {
          const d = new Date(invoiceDay(inv) + 'T00:00:00Z');
          return d >= previousPeriodStart && d < currentPeriodStart;
        });

        // Calculate metrics for current period
        const currentTotalSales = currentPeriodBookings
          .filter(booking => booking.status !== 'cancelled')
          .reduce((sum, booking) => sum + booking.subtotal, 0)
          + currentStandalone.reduce((sum, inv) => sum + inv.subtotal, 0);
        const currentTotalAfterDiscounts = currentPeriodBookings
          .filter(booking => booking.status !== 'cancelled')
          .reduce((sum, booking) => {
            // Get the subtotal
            const subtotal = booking.subtotal;
            // Get the total discount amount
            const discountAmount = booking.discount_amount || 0;
            // Return subtotal minus discounts
            return sum + (subtotal - discountAmount);
          }, 0)
          + sumStandaloneInvoiceSales(currentStandalone);

        // Calculate metrics for previous period
        const previousTotalSales = previousPeriodBookings
          .filter(booking => booking.status !== 'cancelled')
          .reduce((sum, booking) => sum + booking.subtotal, 0)
          + previousStandalone.reduce((sum, inv) => sum + inv.subtotal, 0);
        const previousTotalAfterDiscounts = previousPeriodBookings
          .filter(booking => booking.status !== 'cancelled')
          .reduce((sum, booking) => {
            const subtotal = booking.subtotal;
            const discountAmount = booking.discount_amount || 0;
            return sum + (subtotal - discountAmount);
          }, 0)
          + sumStandaloneInvoiceSales(previousStandalone);

        // Calculate percentage changes
        const salesChange = previousTotalSales === 0 
          ? 100 
          : ((currentTotalSales - previousTotalSales) / previousTotalSales) * 100;

        const afterDiscountsChange = previousTotalAfterDiscounts === 0
          ? 100
          : ((currentTotalAfterDiscounts - previousTotalAfterDiscounts) / previousTotalAfterDiscounts) * 100;

        const currentOrders =
          currentPeriodBookings.filter(booking => booking.status !== 'cancelled').length +
          currentStandalone.length;
        const previousOrders = previousPeriodBookings.length + previousStandalone.length;
        const orderChange = previousOrders === 0
          ? 100
          : ((currentOrders - previousOrders) / previousOrders) * 100;

        // Update sales data
        setSalesData({
          totalSales: currentTotalSales,
          salesChange: Number(salesChange.toFixed(1)),
          totalAfterDiscounts: currentTotalAfterDiscounts,
          afterDiscountsChange: Number(afterDiscountsChange.toFixed(1)),
          totalOrders: currentOrders,
          orderChange: Number(orderChange.toFixed(1)),
          bookings: currentPeriodBookings.filter(booking => booking.status !== 'cancelled')
        });

      } catch (error) {
        console.error('Error fetching sales data:', error);
      }
    };

    if (businessId) {
      fetchSalesData();
    }
  }, [selectedDateRange, customStartDate, customEndDate, businessId]);

  return salesData;
};

// Function to fetch and calculate upsells data
const useUpsellsData = (selectedDateRange: typeof dateRanges[0], customStartDate?: string, customEndDate?: string, businessId?: string) => {
  const [upsellsData, setUpsellsData] = useState({
    totalUpsells: 0,
    upsellsChange: 0
  });

  useEffect(() => {
    const fetchUpsellsData = async () => {
      try {
        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        if (!businessId) {
          console.warn('No business ID provided for upsells data');
          return;
        }

        // Get the date range for comparison
        const now = new Date();
        const currentPeriodStart = new Date();
        const previousPeriodStart = new Date();
        
        // Set up date ranges based on selected period (same logic as useSalesData)
        switch (selectedDateRange.id) {
          case 'this-month':
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);
            previousPeriodStart.setDate(1);
            break;
          case 'last-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 2);
            previousPeriodStart.setDate(1);
            break;
          case 'next-month':
            currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
            currentPeriodStart.setDate(1);
            previousPeriodStart.setMonth(previousPeriodStart.getMonth());
            previousPeriodStart.setDate(1);
            break;
          case 'this-week':
            currentPeriodStart.setDate(currentPeriodStart.getDate() - currentPeriodStart.getDay() + (currentPeriodStart.getDay() === 0 ? -6 : 1));
            previousPeriodStart.setDate(previousPeriodStart.getDate() - 7 - previousPeriodStart.getDay() + (previousPeriodStart.getDay() === 0 ? -6 : 1));
            break;
          case 'last-week':
            currentPeriodStart.setDate(currentPeriodStart.getDate() - 7 - currentPeriodStart.getDay() + (currentPeriodStart.getDay() === 0 ? -6 : 1));
            previousPeriodStart.setDate(previousPeriodStart.getDate() - 14 - previousPeriodStart.getDay() + (previousPeriodStart.getDay() === 0 ? -6 : 1));
            break;
          case 'next-week':
            currentPeriodStart.setDate(currentPeriodStart.getDate() + 7 - currentPeriodStart.getDay() + (currentPeriodStart.getDay() === 0 ? -6 : 1));
            previousPeriodStart.setDate(previousPeriodStart.getDate() - previousPeriodStart.getDay() + (previousPeriodStart.getDay() === 0 ? -6 : 1));
            break;
          case 'custom':
            if (customStartDate && customEndDate) {
              currentPeriodStart.setTime(new Date(customStartDate).getTime());
              // For custom range, calculate previous period of same length
              const daysDiff = Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24));
              previousPeriodStart.setTime(new Date(customStartDate).getTime());
              previousPeriodStart.setDate(previousPeriodStart.getDate() - daysDiff);
            }
            break;
        }

        // Fetch bookings with upsells data
        const { data: bookings, error } = await supabase
          .from('bookings')
          .select('*')
          .eq('business_id', businessId)
          .eq('is_deleted', false)
          .not('upsells', 'is', null) // Only get bookings that have upsells data
          .order('end_date', { ascending: false });

        if (error) throw error;

        // Filter bookings for current period based on end_date and exclude cancelled bookings
        const currentPeriodBookings = (bookings || []).filter(booking => 
          isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
          booking.status !== 'cancelled'
        );

        // Filter bookings for previous period based on end_date and exclude cancelled bookings
        const previousPeriodBookings = (bookings || []).filter(booking => {
          if (!booking.end_date) return false;
          const bookingDate = new Date(booking.end_date + 'T00:00:00Z');
          return bookingDate >= previousPeriodStart && 
                 bookingDate < currentPeriodStart &&
                 booking.status !== 'cancelled';
        });

        // Calculate total upsells for current period
        const currentTotalUpsells = currentPeriodBookings.reduce((sum, booking) => {
          return sum + (booking.upsells || 0);
        }, 0);

        // Calculate total upsells for previous period
        const previousTotalUpsells = previousPeriodBookings.reduce((sum, booking) => {
          return sum + (booking.upsells || 0);
        }, 0);

        // Calculate percentage change
        const upsellsChange = previousTotalUpsells === 0 
          ? (currentTotalUpsells > 0 ? 100 : 0)
          : ((currentTotalUpsells - previousTotalUpsells) / previousTotalUpsells) * 100;

        setUpsellsData({
          totalUpsells: currentTotalUpsells,
          upsellsChange: Number(upsellsChange.toFixed(1))
        });

      } catch (error) {
        console.error('Error fetching upsells data:', error);
      }
    };

    if (businessId) {
      fetchUpsellsData();
    }
  }, [selectedDateRange, customStartDate, customEndDate, businessId]);

  return upsellsData;
};

export default function Sales() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedBusiness } = useBusiness();
  const [selectedDateRange, setSelectedDateRange] = useState(dateRanges[0]);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const salesData = useSalesData(selectedDateRange, customStartDate, customEndDate, selectedBusiness?.id);

  const handlePageSelect = (path: string) => {
    setIsDropdownOpen(false);
    navigate(path);
  };

  const handleDateSelect = (dateRange: typeof dateRanges[0]) => {
    setSelectedDateRange(dateRange);
    if (dateRange.id === 'custom') {
      setIsCustomRangeOpen(true);
    } else {
      setIsCustomRangeOpen(false);
      setIsDateDropdownOpen(false);
    }
  };

  const handleCustomRangeSubmit = () => {
    setIsDateDropdownOpen(false);
    setIsCustomRangeOpen(false);
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const getCurrentPageName = () => {
    return reportPages.find(page => page.path === location.pathname)?.name || 'Sales Reports';
  };

  const metrics = [
    {
      id: 'total-sales',
      name: 'Total Sales',
      value: `$${salesData.totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      change: salesData.salesChange > 0 ? `+${salesData.salesChange.toFixed(1)}%` : `${salesData.salesChange.toFixed(1)}%`,
      trend: salesData.salesChange >= 0 ? 'up' : 'down',
      icon: DollarSign
    },
    {
      id: 'total-after-discounts',
      name: 'Total Sales After',
      value: `$${salesData.totalAfterDiscounts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subValue: `Discounts`,
      change: salesData.afterDiscountsChange > 0 ? `+${salesData.afterDiscountsChange.toFixed(1)}%` : `${salesData.afterDiscountsChange.toFixed(1)}%`,
      trend: salesData.afterDiscountsChange >= 0 ? 'up' : 'down',
      icon: DollarSign
    },
    {
      id: 'scheduled-jobs',
      name: 'Scheduled Jobs',
      value: salesData.totalOrders,
      change: salesData.orderChange > 0 ? `+${salesData.orderChange.toFixed(1)}%` : `${salesData.orderChange.toFixed(1)}%`,
      trend: salesData.orderChange >= 0 ? 'up' : 'down',
      icon: ShoppingBag
    },
    {
      id: 'total-upsells',
      name: 'Total Upsells',
      value: salesData.bookings
        .filter((booking: Booking) => 
          isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
          booking.status !== 'cancelled' &&
          booking.services && booking.services.length > 1
        ).length,
      change: '+11%',
      trend: 'up',
      icon: ArrowUpRight
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/50"
         style={{
           touchAction: 'manipulation',
           WebkitTouchCallout: 'none',
           WebkitUserSelect: 'none',
           paddingTop: 'env(safe-area-inset-top)',
           paddingBottom: 'env(safe-area-inset-bottom)',
           paddingLeft: 'env(safe-area-inset-left)',
           paddingRight: 'env(safe-area-inset-right)',
           fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
         }}>
      {/* Header with Mobile-Optimized Navigation */}
      <div className="backdrop-blur-xl bg-white/70 border-b border-white/20 shadow-sm"
           style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
        <div className="px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            {/* Page Title */}
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sales Reports</h1>
            </div>
            
                         {/* Controls */}
             <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
               {/* Page Selector */}
               <PortalDropdown
                 isOpen={isDropdownOpen}
                 onClose={() => setIsDropdownOpen(false)}
                 align="right"
                 trigger={
                   <button
                     type="button"
                     onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                     className="w-full sm:w-auto backdrop-blur-sm bg-white/60 border border-white/30 rounded-xl px-4 py-3 
                               text-sm font-medium text-gray-700 hover:bg-white/80 transition-all duration-200 
                               flex items-center justify-between gap-2 shadow-sm touch-manipulation"
                     style={{ minHeight: '44px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                   >
                     <span className="text-gray-900 truncate">{getCurrentPageName()}</span>
                     <ChevronDown 
                       className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                     />
                   </button>
                 }
               >
                 <div className="py-2">
                   {reportPages.map((page) => {
                     const Icon = page.icon;
                     const isActive = page.path === location.pathname;
                     return (
                       <button
                         key={page.id}
                         onClick={() => handlePageSelect(page.path)}
                         className={`w-full flex items-center px-4 py-3 text-sm transition-all duration-200 touch-manipulation ${
                           isActive 
                             ? 'bg-purple-50/50 text-purple-600' 
                             : 'text-gray-700 hover:bg-white/50'
                         }`}
                       >
                         <Icon className={`h-4 w-4 mr-3 ${isActive ? 'text-purple-500' : 'text-gray-400'}`} />
                         {page.name}
                       </button>
                     );
                   })}
                 </div>
               </PortalDropdown>

                             {/* Date Selector */}
               <PortalDropdown
                 isOpen={isDateDropdownOpen}
                 onClose={() => {
                   setIsDateDropdownOpen(false);
                   setIsCustomRangeOpen(false);
                 }}
                 align="right"
                 trigger={
                   <button
                     type="button"
                     onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                     className="w-full sm:w-auto backdrop-blur-sm bg-white/60 border border-white/30 rounded-xl px-4 py-3 
                               text-sm font-medium text-gray-700 hover:bg-white/80 transition-all duration-200 
                               flex items-center justify-between gap-2 shadow-sm touch-manipulation"
                     style={{ minHeight: '44px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                   >
                     <div className="flex items-center gap-2 min-w-0">
                       <Calendar className="h-4 w-4 flex-shrink-0 text-purple-500" />
                       <span className="text-gray-900 truncate">{getDateRangeText(selectedDateRange.id, customStartDate, customEndDate)}</span>
                     </div>
                     <ChevronDown 
                       className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform duration-200 ${isDateDropdownOpen ? 'rotate-180' : ''}`} 
                     />
                   </button>
                 }
                 className="w-full sm:w-64"
               >
                 <div className="py-2">
                   {dateRanges.map((dateRange) => {
                     const isActive = dateRange.id === selectedDateRange.id;
                     return (
                       <button
                         key={dateRange.id}
                         onClick={() => handleDateSelect(dateRange)}
                         className={`w-full flex items-center px-4 py-3 text-sm transition-all duration-200 touch-manipulation ${
                           isActive 
                             ? 'bg-purple-50/50 text-purple-600' 
                             : 'text-gray-700 hover:bg-white/50'
                         }`}
                       >
                         {dateRange.icon && <dateRange.icon className={`h-4 w-4 mr-3 ${isActive ? 'text-purple-500' : 'text-gray-400'}`} />}
                         {dateRange.name}
                       </button>
                     );
                   })}
                 </div>

                 {/* Custom Range Selector */}
                 {isCustomRangeOpen && (
                   <div className="border-t border-white/20 p-4 bg-white/30"
                        style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                     <div className="space-y-3">
                       <div>
                         <label htmlFor="start-date" className="block text-xs font-medium text-gray-700 mb-1">
                           Start Date
                         </label>
                         <input
                           type="date"
                           id="start-date"
                           value={customStartDate}
                           onChange={(e) => setCustomStartDate(e.target.value)}
                           className="block w-full px-3 py-2 text-sm bg-white/70 border border-white/30 rounded-xl 
                                     focus:outline-none focus:ring-2 focus:ring-purple-500/20 backdrop-blur-sm touch-manipulation"
                           style={{ minHeight: '36px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                         />
                       </div>
                       <div>
                         <label htmlFor="end-date" className="block text-xs font-medium text-gray-700 mb-1">
                           End Date
                         </label>
                         <input
                           type="date"
                           id="end-date"
                           value={customEndDate}
                           onChange={(e) => setCustomEndDate(e.target.value)}
                           className="block w-full px-3 py-2 text-sm bg-white/70 border border-white/30 rounded-xl 
                                     focus:outline-none focus:ring-2 focus:ring-purple-500/20 backdrop-blur-sm touch-manipulation"
                           style={{ minHeight: '36px', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                         />
                       </div>
                       <button
                         onClick={handleCustomRangeSubmit}
                         className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-xl 
                                  hover:bg-purple-700 transition-colors duration-200 shadow-sm touch-manipulation"
                         style={{ minHeight: '36px' }}
                       >
                         Apply Range
                       </button>
                     </div>
                   </div>
                 )}
               </PortalDropdown>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="flex-1 p-3 sm:p-6 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {metrics.map((metric) => {
              const Icon = metric.icon;
              return (
                <div
                  key={metric.id}
                  className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 sm:p-3 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl shadow-lg">
                        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-600 line-clamp-2">{metric.name}</span>
                    </div>
                    <div className={`flex items-center ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                      {metric.trend === 'up' ? (
                        <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4" />
                      ) : (
                        <ArrowDownRight className="h-3 w-3 sm:h-4 sm:w-4" />
                      )}
                      <span className="text-xs sm:text-sm font-medium ml-1">{metric.change}</span>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{metric.value}</span>
                    {'subValue' in metric && (
                      <span className="text-xs sm:text-sm text-gray-500">{metric.subValue}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Status Distribution Bar */}
          <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm sm:text-lg font-bold text-gray-900">Sales by Status</h3>
            </div>
            <div className="h-8 sm:h-10 bg-gray-100/50 backdrop-blur-sm rounded-2xl overflow-hidden flex relative border border-white/30"
                 style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
              {salesData.totalAfterDiscounts > 0 && ['created', 'scheduled', 'in_progress', 'completed'].map((status) => {
                const totalSales = salesData.bookings
                  .filter((booking: Booking) => 
                    isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
                    booking.status === status &&
                    booking.status !== 'cancelled'
                  )
                  .reduce((sum: number, booking: Booking) => {
                    if (booking.status === status) {
                      return sum + (booking.subtotal - (booking.discount_amount || 0));
                    }
                    return sum;
                  }, 0);

                const percentage = (totalSales / salesData.totalAfterDiscounts) * 100;

                if (totalSales === 0) return null;

                return (
                  <div
                    key={status}
                    className={`group h-full relative ${
                      status === 'created' ? 'bg-gray-400 hover:bg-gray-500' :
                      status === 'scheduled' ? 'bg-blue-400 hover:bg-blue-500' :
                      status === 'in_progress' ? 'bg-yellow-400 hover:bg-yellow-500' :
                      'bg-green-400 hover:bg-green-500'
                    } transition-all duration-200`}
                    style={{ width: `${percentage}%` }}
                  >
                    {/* Sales amount - hidden on hover */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
                      <span className="text-xs font-bold text-white px-2 truncate">
                        ${totalSales.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    {/* Status title - visible on hover */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/20 transition-opacity duration-200">
                      <span className="text-xs font-bold text-white px-2 truncate">
                        {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sales Reports Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Sales By Service */}
            <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg"
                 style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm sm:text-lg font-bold text-gray-900">Sales By Service / Item</h2>
              </div>
              <div className="space-y-3 max-h-[300px] sm:max-h-[400px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                {Object.values(salesData.bookings
                  .filter((booking: Booking) => 
                    isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
                    booking.status !== 'cancelled'
                  )
                  .reduce((acc: Record<string, ServiceCategory>, booking: Booking) => {
                    // Process each service in the booking
                    booking.services?.forEach((service: any) => {
                      // Get the appropriate category name based on whether it's a service or sellable item
                      const categoryName = service.service_id 
                        ? (service.service_category_name || 'Uncategorized Services')
                        : (service.category_name || 'Uncategorized Items');

                      if (!acc[categoryName]) {
                        acc[categoryName] = {
                          id: service.id || 'uncategorized',
                          name: categoryName,
                          totalSales: 0,
                          services: []
                        };
                      }

                      // Find or create the service entry
                      let serviceEntry = acc[categoryName].services.find(s => 
                        service.service_id 
                          ? s.id === service.service_id  // For services
                          : s.id === service.sellable_item_id  // For items
                      );
                      
                      if (!serviceEntry) {
                        serviceEntry = {
                          id: service.service_id || service.sellable_item_id || service.id,  // Fallback to service.id if neither is available
                          name: service.name || service.service_name || 'Unnamed Service',
                          sales: 0,
                          revenue: 0
                        };
                        acc[categoryName].services.push(serviceEntry);
                      }

                      // Update service metrics
                      if (serviceEntry) {  // TypeScript check
                        serviceEntry.sales += 1;
                        serviceEntry.revenue += (service.price || 0) - (service.discount_amount || 0);
                        acc[categoryName].totalSales += (service.price || 0) - (service.discount_amount || 0);
                      }
                    });
                    return acc;
                  }, {}))
                  .sort((a, b) => b.totalSales - a.totalSales)
                  .map((category: ServiceCategory) => (
                    <div key={category.id} className="glassmorphism-card overflow-hidden">
                      <button 
                        onClick={() => toggleCategory(category.id)}
                        className="w-full flex items-center justify-between p-3 hover:bg-white/30 transition-all duration-200 touch-manipulation"
                        style={{ minHeight: '44px' }}
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-2 border border-white/30"
                               style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                            <ChevronDown 
                              className={`h-4 w-4 text-purple-600 transition-transform duration-200 ${
                                expandedCategories.has(category.id) ? 'rotate-180' : ''
                              }`} 
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm sm:text-base font-bold text-gray-900 truncate">{category.name}</p>
                            <p className="text-xs text-gray-500">
                              {category.services.length} {category.services.length === 1 ? 'service' : 'services'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right ml-3">
                          <p className="text-sm sm:text-base font-bold text-gray-900">
                            ${category.totalSales.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </p>
                          <p className="text-xs font-medium text-purple-600">
                            {((category.totalSales / salesData.totalSales) * 100).toFixed(1)}% of total
                          </p>
                        </div>
                      </button>
                      {expandedCategories.has(category.id) && (
                        <div className="border-t border-white/20 bg-white/20 backdrop-blur-sm"
                             style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                          {Object.values(category.services
                            .reduce((acc: Record<string, { id: string; name: string; sales: number; revenue: number }>, service) => {
                              const key = service.id;
                              if (!acc[key]) {
                                acc[key] = {
                                  id: service.id,
                                  name: service.name,
                                  sales: 0,
                                  revenue: 0
                                };
                              }
                              acc[key].sales += service.sales;
                              acc[key].revenue += service.revenue;
                              return acc;
                            }, {}))
                            .sort((a, b) => b.revenue - a.revenue)
                            .map((service) => (
                              <div 
                                key={service.id} 
                                className="flex items-center justify-between p-3 border-b border-white/10 last:border-b-0 hover:bg-white/20 transition-all duration-200"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm text-gray-800 truncate font-medium">{service.name}</p>
                                  <p className="text-xs text-gray-600">{service.sales} sales</p>
                                </div>
                                <div className="text-right ml-3">
                                  <p className="text-sm font-bold text-gray-800">
                                    ${service.revenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </p>
                                  <p className="text-xs font-medium text-purple-600">
                                    {((service.revenue / category.totalSales) * 100).toFixed(1)}% of category
                                  </p>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            {/* Sales Chart */}
            <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg"
                 style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm sm:text-lg font-bold text-gray-900">Sales Trend</h2>
              </div>
              <div className="h-[250px] sm:h-[350px]">
                {(() => {
                  // Group sales by date (excluding cancelled bookings)
                  const salesByDate = salesData.bookings
                    .filter((booking: Booking) => 
                      isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
                      booking.status !== 'cancelled'
                    )
                    .reduce((acc: Record<string, number>, booking: Booking) => {
                      if (!booking.end_date) return acc;
                      
                      // Convert to UTC date string (YYYY-MM-DD)
                      const date = new Date(booking.end_date + 'T00:00:00Z');
                      const dateKey = date.toISOString().split('T')[0];
                      
                      if (!acc[dateKey]) {
                        acc[dateKey] = 0;
                      }
                      acc[dateKey] += booking.subtotal - (booking.discount_amount || 0);
                      return acc;
                    }, {});

                  // Sort dates and prepare chart data
                  const dates = Object.keys(salesByDate).sort();
                  const sales = dates.map(date => salesByDate[date]);

                  const chartData = {
                    labels: dates.map(date => {
                      const d = new Date(date + 'T00:00:00Z');
                      return d.toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        timeZone: 'UTC'
                      });
                    }),
                    datasets: [
                      {
                        label: 'Daily Sales',
                        data: sales,
                        borderColor: 'rgb(147, 51, 234)',
                        backgroundColor: 'rgba(147, 51, 234, 0.1)',
                        tension: 0.4,
                        fill: true,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                        pointBackgroundColor: 'rgb(147, 51, 234)',
                        pointBorderColor: 'white',
                        pointBorderWidth: 2
                      }
                    ]
                  };

                  const options = {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        titleColor: 'rgb(17, 24, 39)',
                        bodyColor: 'rgb(17, 24, 39)',
                        borderColor: 'rgba(147, 51, 234, 0.2)',
                        borderWidth: 1,
                        cornerRadius: 12,
                        callbacks: {
                          label: (context: any) => {
                            return `$${context.parsed.y.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(147, 51, 234, 0.1)'
                        },
                        ticks: {
                          callback: (value: any) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                        }
                      },
                      x: {
                        grid: {
                          display: false
                        }
                      }
                    }
                  };

                  return (
                    <Line data={chartData} options={options} />
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Sales History Table */}
          <div className="backdrop-blur-xl bg-white/70 border border-white/20 rounded-2xl p-4 sm:p-6 shadow-lg"
               style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm sm:text-lg font-bold text-gray-900">Sales History</h2>
            </div>
            <div className="overflow-x-auto -mx-4 sm:-mx-6">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden rounded-xl border border-white/30"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <div className="max-h-[400px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                    <table className="min-w-full divide-y divide-white/20">
                      <thead className="bg-white/30 backdrop-blur-sm sticky top-0 z-10"
                             style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            Booking #
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            Booking
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            Date
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            Amount
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white/20 backdrop-blur-sm divide-y divide-white/20"
                             style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                        {salesData.bookings
                          .filter((booking: Booking) => 
                            isDateInRange(booking.end_date, selectedDateRange.id, customStartDate, customEndDate) &&
                            booking.status !== 'cancelled'
                          )
                          .map((booking: Booking) => (
                            <tr key={booking.id} className="hover:bg-white/30 transition-colors duration-200">
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap font-medium">
                                {booking.booking_number || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                <div className="line-clamp-1">{booking.title}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {booking.end_date 
                                  ? new Date(booking.end_date + 'T00:00:00Z').toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      timeZone: 'UTC'
                                    })
                                  : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-bold whitespace-nowrap">
                                ${(booking.subtotal - (booking.discount_amount || 0)).toLocaleString('en-US', { 
                                  minimumFractionDigits: 2, 
                                  maximumFractionDigits: 2 
                                })}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold backdrop-blur-sm border ${
                                    booking.status === 'completed'
                                      ? 'bg-green-100/70 text-green-800 border-green-200/50'
                                      : booking.status === 'cancelled'
                                      ? 'bg-red-100/70 text-red-800 border-red-200/50'
                                      : 'bg-yellow-100/70 text-yellow-800 border-yellow-200/50'
                                  }`}
                                  style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
                                >
                                  {booking.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 